import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  CircularProgress,
  Dialog,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import CloseIcon from "@mui/icons-material/Close"
import MenuBookIcon from "@mui/icons-material/MenuBook"

import {
  getOrganizationsEnterpriseSelector,
  getCatalogProductTypesSelector,
} from "../../../../reducers/Organizations/organizations"
import { saveAndSyncEnterprisePackagingSettings } from "../../../../actions/Organizations/Enterprise"

const getCatalogKfcNames = (enterprise) => {
  const seen = new Set()
  const result = []
  for (const source of (enterprise?.catalogSources || [])) {
    for (const kfcName of (source.productTypes || [])) {
      if (!seen.has(kfcName)) {
        seen.add(kfcName)
        result.push(kfcName)
      }
    }
  }
  return result
}

const PackagingSettingsModal = ({ open, onClose, onSave, onGoToCatalog }) => {
  const dispatch = useDispatch()
  const enterprise = useSelector(getOrganizationsEnterpriseSelector)
  const catalogProductTypes = useSelector(getCatalogProductTypesSelector)
  const packagings = useSelector((state) => state.packagingList.packagingList)

  const catalogKfcNames = getCatalogKfcNames(enterprise)

  const enterprisePackagings = packagings.filter(p =>
    (p.toJSON().enterprises || []).some(e => e.objectId === enterprise?.objectId)
  )
  const filteredPackagings = enterprisePackagings.filter(p => p.get("type") === "1" || p.get("type") === "0")
  const filteredSubPackagings = enterprisePackagings.filter(p => p.get("type") === "2" || p.get("type") === "0")

  const getLabel = (kfcName) => {
    const pt = catalogProductTypes.find((p) => p.kfcName === kfcName)
    return pt ? pt.label : kfcName
  }

  const [drafts, setDrafts] = useState(() => {
    const initial = {}
    for (const s of (enterprise?.packagingSettings || [])) {
      initial[s.productType] = {
        packagingId: s.packagingId || "",
        subPackagingId: s.subPackagingId || "",
        dlc: s.dlc || "",
      }
    }
    return initial
  })

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const setDraft = (kfcName, field, value) => {
    setDrafts((prev) => ({ ...prev, [kfcName]: { ...prev[kfcName], [field]: value } }))
  }

  const isRowFilled = (d) => d && (d.packagingId || d.subPackagingId || d.dlc)

  const validate = () => {
    const hasAny = catalogKfcNames.some(k => isRowFilled(drafts[k]))
    if (!hasAny) return "Complétez au moins une catégorie."
    for (const kfcName of catalogKfcNames) {
      const d = drafts[kfcName]
      if (!d || !d.dlc) continue
      const dlc = Number(d.dlc)
      if (!Number.isInteger(dlc) || dlc < 1) {
        return `La DLC de "${getLabel(kfcName)}" doit être un entier d'au moins 1 jour.`
      }
    }
    return null
  }

  const handleSync = async () => {
    const err = validate()
    if (err) { setError(err); return }
    setError(null)
    setBusy(true)
    const settings = catalogKfcNames
      .filter((k) => isRowFilled(drafts[k]))
      .map((k) => ({
        productType: k,
        packagingId: drafts[k].packagingId || null,
        subPackagingId: drafts[k].subPackagingId || null,
        dlc: drafts[k].dlc ? Number(drafts[k].dlc) : null,
      }))
    const result = await dispatch(saveAndSyncEnterprisePackagingSettings(enterprise.objectId, settings))
    setBusy(false)
    if (result !== false) onSave(result)
  }

  const isEmpty = catalogKfcNames.length === 0

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: isEmpty ? sx.paperNarrow : sx.paper }} maxWidth={false}>
      {/* create-modal-header */}
      <Box sx={sx.header}>
        <Typography sx={sx.title}>Paramétrer le conditionnement</Typography>
        <Box component="button" onClick={onClose} aria-label="Fermer" sx={sx.closeBtn}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      {/* create-modal-body */}
      <Box sx={isEmpty ? sx.bodyEmpty : sx.body}>
        {isEmpty ? (
          <Box sx={sx.emptyState}>
            <Box sx={sx.emptyIconCircle} aria-hidden="true">
              <MenuBookIcon sx={{ fontSize: 32, color: "#7C7C7C" }} />
            </Box>
            <Typography sx={sx.emptyTitle}>
              {"Complétez d'abord le catalogue de recettes"}
            </Typography>
            <Typography sx={sx.emptySubtitle}>
              {"Aucune catégorie de recettes n'est disponible. Générez le catalogue de recettes de l'entreprise pour pouvoir paramétrer le conditionnement."}
            </Typography>
          </Box>
        ) : (
          <Box sx={sx.condList}>
            {/* cond-list-head */}
            <Box sx={sx.condListHead}>
              <Typography sx={sx.condHeadCell}>Type de produits</Typography>
              <Typography sx={sx.condHeadCell}>Packaging</Typography>
              <Typography sx={sx.condHeadCell}>Sous-packaging</Typography>
              <Typography sx={sx.condHeadCell}>DLC</Typography>
            </Box>

            {/* cond-list-rows */}
            {catalogKfcNames.map((kfcName, idx) => {
              const d = drafts[kfcName] || { packagingId: "", subPackagingId: "", dlc: "" }
              const isLast = idx === catalogKfcNames.length - 1
              return (
                <Box key={kfcName} sx={{ ...sx.condListRow, ...(isLast ? sx.condListRowLast : {}) }}>
                  <Typography sx={sx.condGridCat}>{getLabel(kfcName)}</Typography>

                  <FormControl variant="standard" fullWidth>
                    <Select
                      value={d.packagingId}
                      onChange={(e) => setDraft(kfcName, "packagingId", e.target.value)}
                      displayEmpty
                      disableUnderline={false}
                      sx={sx.select}
                    >
                      <MenuItem value=""><em style={{ color: "#9B9B9B" }}>Packaging</em></MenuItem>
                      {filteredPackagings.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.get("name")}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl variant="standard" fullWidth>
                    <Select
                      value={d.subPackagingId}
                      onChange={(e) => setDraft(kfcName, "subPackagingId", e.target.value)}
                      displayEmpty
                      sx={sx.select}
                    >
                      <MenuItem value=""><em style={{ color: "#9B9B9B" }}>Aucun — par défaut</em></MenuItem>
                      {filteredSubPackagings.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.get("name")}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box
                    component="input"
                    type="number"
                    min={1}
                    max={90}
                    step={1}
                    value={d.dlc}
                    onChange={(e) => setDraft(kfcName, "dlc", e.target.value)}
                    sx={sx.dlcInput}
                  />
                </Box>
              )
            })}
          </Box>
        )}

      </Box>

      {error && <Typography sx={sx.errorBanner}>{error}</Typography>}

      {/* create-modal-footer */}
      <Box sx={sx.footer}>
        <Box component="button" onClick={onClose} disabled={busy} sx={sx.btnText}>
          Annuler
        </Box>
        {isEmpty ? (
          <Box
            component="button"
            onClick={onGoToCatalog || onClose}
            sx={sx.btnFilled}
          >
            Aller au catalogue de recettes
            <ArrowForwardIcon sx={{ fontSize: 18, ml: "6px" }} />
          </Box>
        ) : (
          <Box
            component="button"
            onClick={handleSync}
            disabled={busy}
            sx={sx.btnFilled}
          >
            {busy ? <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} /> : null}
            Synchroniser
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

const sx = {
  paperNarrow: {
    width: 560,
    maxWidth: "calc(100vw - 64px)",
    maxHeight: "calc(100vh - 64px)",
    borderRadius: "12px",
    boxShadow: "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
  },
  paper: {
    width: 820,
    maxWidth: "calc(100vw - 64px)",
    maxHeight: "calc(100vh - 64px)",
    borderRadius: "12px",
    boxShadow: "0 12px 40px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    px: "40px",
    pt: "32px",
    pb: "8px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    flexShrink: 0,
  },
  title: {
    fontSize: 20,
    fontWeight: 400,
    color: "rgba(0,0,0,0.87)",
    lineHeight: 1.3,
  },
  closeBtn: {
    width: 36,
    height: 36,
    border: "none",
    background: "transparent",
    color: "#555555",
    cursor: "pointer",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mt: "-8px",
    mr: "-8px",
    flexShrink: 0,
    transition: "background 150ms",
    "&:hover": { bgcolor: "#F0F0F0" },
  },
  body: {
    px: "40px",
    pt: "24px",
    pb: "8px",
    overflowY: "auto",
    flex: 1,
  },
  bodyEmpty: {
    flex: 1,
    overflowY: "auto",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    p: "32px 40px",
    gap: "16px",
  },
  emptyIconCircle: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    bgcolor: "#F5F5F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: "8px",
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 500,
    color: "#262626",
    lineHeight: 1.3,
    letterSpacing: "-0.005em",
  },
  emptySubtitle: {
    fontSize: 14,
    color: "#7C7C7C",
    maxWidth: 380,
    lineHeight: 1.6,
  },
  condList: {
    borderRadius: "8px",
  },
  condListHead: {
    display: "grid",
    gridTemplateColumns: "140px 1.6fr 1.6fr 0.6fr",
    columnGap: "24px",
    px: "16px",
    py: "12px",
    bgcolor: "#EDF4FB",
    borderRadius: "8px 8px 0 0",
  },
  condHeadCell: {
    fontSize: 16,
    fontWeight: 500,
    color: "#555555",
  },
  condListRow: {
    display: "grid",
    gridTemplateColumns: "140px 1.6fr 1.6fr 0.6fr",
    columnGap: "24px",
    px: "16px",
    py: "12px",
    alignItems: "end",
    borderTop: "1px solid #EDEDED",
    borderLeft: "1px solid #E6E6E6",
    borderRight: "1px solid #E6E6E6",
    transition: "background 120ms",
    "&:hover": { bgcolor: "#FAFAFA" },
  },
  condListRowLast: {
    borderBottom: "1px solid #E6E6E6",
    borderRadius: "0 0 8px 8px",
  },
  condGridCat: {
    fontSize: 16,
    fontWeight: 500,
    color: "#262626",
    alignSelf: "center",
    pb: 0,
  },
  select: {
    fontSize: 16,
    "& .MuiSelect-select": { pb: "8px" },
  },
  dlcInput: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #E0E0E0",
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 16,
    color: "#262626",
    p: "6px 0",
    outline: "none",
    "&:focus": {
      borderBottom: "2px solid #2196F3",
    },
  },
  errorBanner: {
    color: "error.main",
    fontSize: 13,
    px: "40px",
    py: "8px",
    flexShrink: 0,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    px: "40px",
    pt: "24px",
    pb: "32px",
    flexShrink: 0,
  },
  btnText: {
    background: "transparent",
    border: "none",
    color: "#2196F3",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    px: "28px",
    py: "12px",
    borderRadius: "4px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    ml: "-28px",
    transition: "background 150ms",
    "&:hover": { bgcolor: "#E3F2FD" },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
  btnFilled: {
    background: "#2196F3",
    border: "none",
    color: "#fff",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 500,
    px: "28px",
    py: "12px",
    borderRadius: "4px",
    cursor: "pointer",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    boxShadow: "0 2px 4px rgba(33,150,243,0.3)",
    display: "inline-flex",
    alignItems: "center",
    transition: "box-shadow 150ms, background 150ms",
    "&:hover": { boxShadow: "0 4px 8px rgba(33,150,243,0.4)" },
    "&:disabled": { opacity: 0.5, cursor: "default" },
  },
}

export default PackagingSettingsModal
